/* eslint-disable max-lines -- one file of exhaustive zod mirrors, same convention as helpers/apis.ts */
import { z } from "zod";

/**
 * Zod mirrors of dash/v2/v2.types.ts.
 *
 * Descriptor/meta object schemas use `.passthrough()` so a backend field
 * addition doesn't fail validation before the frontend has been updated to
 * know about it.
 *
 * Envelope `rows` are deliberately `z.array(z.record(z.unknown()))` rather
 * than a strict per-column shape: `api_helper.ts`'s `applyCatchToSchema`
 * silently coerces any failing string/number leaf to `""` on a schema
 * mismatch, so a strict row schema would blank real cell values instead of
 * surfacing the mismatch. Row values are read dynamically by column `key`
 * at render time, not validated field-by-field.
 */

export const ColumnUnitSchema = z.enum([
  "currency",
  "credits",
  "count",
  "ms",
  "bytes",
  "percent",
  "tokens",
  "seconds",
  "minutes",
]);

export const ColumnTypeSchema = z.enum([
  "string",
  "number",
  "date",
  "datetime",
  "boolean",
  "string[]",
]);

export const ToneSchema = z.enum(["neutral", "brand", "success", "warning", "danger", "info"]);

export const PaletteColorSchema = z
  .object({
    light: z.string(),
    dark: z.string(),
  })
  .passthrough();

export const LinkDescriptorSchema = z
  .object({
    template: z.string(),
  })
  .passthrough();

export const EditDescriptorSchema = z
  .object({
    action: z.string(),
    options_from: z.string().nullable(),
  })
  .passthrough();

export const ValueStyleDescriptorSchema = z
  .object({
    value: z.string(),
    tone: ToneSchema,
    label: z.string().nullable(),
    icon: z.string().nullable(),
  })
  .passthrough();

export const ThresholdDescriptorSchema = z
  .object({
    tone: ToneSchema,
    min: z.number().nullable(),
    max: z.number().nullable(),
  })
  .passthrough();

export const DecorationDescriptorSchema = z
  .object({
    display: z.enum(["text", "badge", "pills", "snippet", "progress"]),
    tone: ToneSchema.nullable(),
    values: z.array(ValueStyleDescriptorSchema),
    thresholds: z.array(ThresholdDescriptorSchema),
    hash_column: z.string().nullable().optional(),
  })
  .passthrough();

export const ComposeWhenDescriptorSchema = z
  .object({
    column: z.string(),
    equals: z.string(),
  })
  .passthrough();

export const ComposePartDescriptorSchema = z
  .object({
    column: z.string(),
    tone: ToneSchema.nullable(),
  })
  .passthrough();

export const ComposeLineDescriptorSchema = z
  .object({
    parts: z.array(ComposePartDescriptorSchema),
    label: z.string().nullable(),
    separator: z.string(),
    when: ComposeWhenDescriptorSchema.nullable(),
  })
  .passthrough();

export const ComposeDescriptorSchema = z
  .object({
    lines: z.array(ComposeLineDescriptorSchema),
    badge: z.string().nullable(),
    show_value: z.boolean(),
  })
  .passthrough();

export const ColumnDescriptorSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    type: ColumnTypeSchema,
    unit: ColumnUnitSchema.nullable(),
    sortable: z.boolean(),
    filterable: z.boolean(),
    searchable: z.boolean(),
    hideable: z.boolean(),
    hidden_by_default: z.boolean(),
    info: z.string().nullable(),
    edit: EditDescriptorSchema.nullable(),
    header: z.boolean(),
    link: LinkDescriptorSchema.nullable(),
    icon: z.string().nullable(),
    decoration: DecorationDescriptorSchema.nullable(),
    compose: ComposeDescriptorSchema.nullable().optional(),
    internal: z.boolean().optional(),
    order: z.number(),
  })
  .passthrough();

export const OptionDescriptorSchema = z
  .object({
    value: z.string(),
    label: z.string().nullable(),
  })
  .passthrough();

export const FilterDescriptorSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    listable: z.boolean(),
    options: z.array(OptionDescriptorSchema).nullable().optional(),
    required: z.boolean().optional(),
  })
  .passthrough();

export const RelatedDescriptorSchema = z
  .object({
    datasets: z.array(z.string()),
    charts: z.array(z.string()),
    summaries: z.array(z.string()),
  })
  .passthrough();

export const DatasetDescriptorSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    platform: z.string(),
    filters: z.array(FilterDescriptorSchema),
    columns: z.array(ColumnDescriptorSchema),
    row_key: z.array(z.string()),
    paginated: z.boolean(),
    time_bounded: z.boolean(),
    searchable: z.boolean(),
    search_placeholder: z.string().nullable().optional(),
    downloadable: z.boolean(),
    related: RelatedDescriptorSchema,
  })
  .passthrough();

export const MetricDescriptorSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    unit: ColumnUnitSchema.nullable(),
    info: z.string().nullable(),
    order: z.number(),
    tone: ToneSchema.nullable(),
    compact: z.boolean().optional(),
    precision: z.number().nullable().optional(),
  })
  .passthrough();

// See the `SummaryDescriptor` comment in types.ts — shape not given by the
// phase-1 spec, kept minimal and re-verify before trusting extra fields.
export const SummaryDescriptorSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    filters: z.array(FilterDescriptorSchema),
    metrics: z.array(MetricDescriptorSchema),
    related: RelatedDescriptorSchema,
  })
  .passthrough();

export const ReferenceLineDescriptorSchema = z
  .object({
    of: z.string().nullable(),
    percentile: z.number().nullable(),
    label: z.string(),
    color: PaletteColorSchema,
  })
  .passthrough();

export const ChartDescriptorSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    dataset: z.string(),
    chart_type: z.enum(["line", "bar", "horizontal_bar", "area", "pie", "table"]),
    x: z.string(),
    series: z.array(z.string()),
    stacked: z.boolean(),
    filters: z.array(FilterDescriptorSchema),
    related: RelatedDescriptorSchema,
    reference: ReferenceLineDescriptorSchema.nullable(),
    series_colors: z.record(PaletteColorSchema),
    series_from: z.string().nullable().optional(),
    show_change: z.boolean().optional(),
    show_total: z.boolean().optional(),
    show_zero: z.boolean().optional(),
    caption: z.string().nullable().optional(),
    stack_offset: z.enum(["none", "sign"]).optional(),
    secondary_axis_series: z.array(z.string()).optional(),
    estimate_pairs: z.array(z.object({ actual: z.string(), estimate: z.string() })).optional(),
    tooltip_series: z.array(z.string()).optional(),
    tooltip_estimate_pairs: z
      .array(z.object({ actual: z.string(), estimate: z.string() }))
      .optional(),
    tooltip_decomposes: z.string().nullable().optional(),
  })
  .passthrough();

export const ChartColumnSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    type: ColumnTypeSchema,
    unit: ColumnUnitSchema.nullable(),
    order: z.number(),
  })
  .passthrough();

export const DatasetMetaSchema = z
  .object({
    row_count: z.number(),
    dataset: z.string(),
    total: z.number().nullable(),
    page: z.number().nullable(),
    size: z.number().nullable(),
    pages: z.number().nullable(),
    prev: z.number().nullable(),
    next: z.number().nullable(),
    last_event_time: z.string().nullable(),
  })
  .passthrough();

export const DatasetEnvelopeSchema = z
  .object({
    columns: z.array(ColumnDescriptorSchema),
    rows: z.array(z.record(z.unknown())),
    meta: DatasetMetaSchema,
  })
  .passthrough();

export const ReferenceValueSchema = z
  .object({
    label: z.string(),
    of: z.string().nullable(),
    value: z.number(),
  })
  .passthrough();

export const ChartMetaSchema = z
  .object({
    row_count: z.number(),
    reference: ReferenceValueSchema.nullable(),
    last_event_time: z.string().nullable(),
  })
  .passthrough();

export const ChartEnvelopeSchema = z
  .object({
    chart: ChartDescriptorSchema,
    columns: z.array(ChartColumnSchema),
    rows: z.array(z.record(z.unknown())),
    meta: ChartMetaSchema,
  })
  .passthrough();

export const MetricValueSchema = z
  .object({
    value: z.number().nullable(),
    display: z.boolean(),
    label: z.string(),
    unit: ColumnUnitSchema.nullable(),
    info: z.string().nullable(),
    order: z.number(),
    tone: ToneSchema.nullable(),
    compact: z.boolean().optional(),
    precision: z.number().nullable().optional(),
  })
  .passthrough();

export const SummaryEnvelopeSchema = z
  .object({
    metrics: z.record(MetricValueSchema),
    last_event_time: z.string().nullable(),
  })
  .passthrough();

export const FilterOptionSchema = z
  .object({
    label: z.string(),
    value: z.string(),
  })
  .passthrough();

export const FilterOptionsMetaSchema = z
  .object({
    count: z.number(),
    page: z.number(),
    size: z.number(),
    has_more: z.boolean(),
  })
  .passthrough();

export const FilterOptionsEnvelopeSchema = z
  .object({
    options: z.array(FilterOptionSchema),
    meta: FilterOptionsMetaSchema,
  })
  .passthrough();

export const DatasetQueryRequestSchema = z.object({
  filters: z.record(z.array(z.string())).optional(),
  start_date: z.string(),
  end_date: z.string(),
  granularity: z.enum(["day", "week", "month"]).optional(),
  limit: z.number().optional(),
  sort: z
    .object({
      key: z.string(),
      direction: z.enum(["asc", "desc"]),
    })
    .optional(),
  search: z.string().optional(),
  page: z.number().optional(),
  size: z.number().optional(),
});

export const ChartQueryRequestSchema = DatasetQueryRequestSchema.omit({
  page: true,
  size: true,
});

export const FilterOptionsParamsSchema = z.object({
  dataset: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  search: z.string().optional(),
  page: z.number().optional(),
  size: z.number().optional(),
});

export const DatasetDescriptorListSchema = z.array(DatasetDescriptorSchema);
export const ChartDescriptorListSchema = z.array(ChartDescriptorSchema);
export const FilterDescriptorListSchema = z.array(FilterDescriptorSchema);
